"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Archive, MoreHorizontal, Pencil, Plus } from "lucide-react";
import { post, put } from "../../lib/api";
import LoadingScreenDenda from "../Unique/Animation/loading_screen";

const GameManager = () => {
  const [games, setGames] = useState([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [newGameName, setNewGameName] = useState("");
  const [newGameVersion, setNewGameVersion] = useState("");
  const [newGameId, setNewGameId] = useState("");
  const [currentGame, setCurrentGame] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const response = await post(
        {
          method: "getGames",
        },
        "/administration"
      );
      setGames(response);
      const usedIds = response.map((g) => parseInt(g.game_id)).filter((id) => !isNaN(id));
      const nextId = usedIds.length > 0 ? Math.max(...usedIds) + 1 : 1;
      setNewGameId(String(nextId));
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const handleAddGame = async () => {
    if (newGameName.trim() && newGameId.trim()) {
      try {
        setIsLoading(true);
        await post(
          {
            game_id: newGameId,
            version: newGameVersion,
            name: newGameName,
          },
          "/games"
        );

        const newGame = {
          game_id: newGameId,
          name: String(newGameName).charAt(0).toUpperCase() + String(newGameName).slice(1),
          version: newGameVersion,
          isArchived: "0",
        };
        setGames([...games, newGame]);
      } catch (error) {
        console.error("Error adding game:", error);
      } finally {
        setIsLoading(false);
        setNewGameVersion("");
        setNewGameName("");
        setIsAddDialogOpen(false);
      }
    }
  };

  const handleEditGame = async () => {
    if (currentGame && newGameName.trim()) {
      try {
        setIsLoading(true);
        await put(
          {
            game_id: currentGame.game_id,
            name: newGameName,
            version: newGameVersion,
            old_name: currentGame.name,
          },
          "/games"
        );

        setGames(games.map((game) => (game.name === currentGame.name ? { ...game, name: newGameName, version: newGameVersion } : game)));
      } catch (error) {
        console.error("Error updating game:", error);
      } finally {
        setIsLoading(false);
        setIsEditDialogOpen(false);
      }
    }
  };

  // Persist archive status to backend and update UI accordingly
  const handleArchiveGame = async (game) => {
    const updatedArchivedStatus = game.isArchived === "1" ? "0" : "1";
    try {
      await put(
        {
          name: game.name,
          isArchived: updatedArchivedStatus,
        },
        "/games"
      );
      setGames(games.map((g) => (g.name === game.name ? { ...g, isArchived: updatedArchivedStatus } : g)));
    } catch (error) {
      console.error("Error archiving game:", error);
    }
  };

  const openEditDialog = (game) => {
    setCurrentGame(game);
    setNewGameVersion(game.version || "");
    setNewGameName(game.name);
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return <LoadingScreenDenda />;
  }

  return (
    <Card className="w-full dark:bg-[var(--header-color-dark)]">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Game Manager</CardTitle>
          <CardDescription>Manage your games collection</CardDescription>
        </div>
        <Button
          onClick={() => {
            setNewGameName("");
            setNewGameVersion("");
            setIsAddDialogOpen(true);
          }}
          className="flex items-center gap-1 bg-purple-700 text-white hover:bg-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-white"
        >
          <Plus className="h-4 w-4" /> Add Game
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {games.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No games found. Add a game to get started.
                </TableCell>
              </TableRow>
            ) : (
              games.map((game) => (
                <TableRow key={game.game_id}>
                  <TableCell className="font-mono text-sm">{game.game_id}</TableCell>
                  <TableCell>{game.name}</TableCell>
                  <TableCell>{game.version || "N/A"}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${game.isArchived === "1" ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800"}`}
                    >
                      {game.isArchived === "1" ? "Archived" : "Active"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(game)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleArchiveGame(game)}>
                          <Archive className="mr-2 h-4 w-4" />
                          {game.isArchived === "1" ? "Unarchive" : "Archive"}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Add Game Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Game</DialogTitle>
            <DialogDescription>Enter the details for the new game.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="gameId">Game ID</label>
              <Input id="gameId" value={newGameId} disabled className="bg-muted" />
            </div>
            <div className="grid gap-2">
              <label htmlFor="gameVersion">Game Version</label>
              <Input id="gameVersion" value={newGameVersion} onChange={(e) => setNewGameVersion(e.target.value)} placeholder="Enter game version" />
            </div>
            <div className="grid gap-2">
              <label htmlFor="gameName">Game Name</label>
              <Input id="gameName" value={newGameName} onChange={(e) => setNewGameName(e.target.value)} placeholder="Enter game name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddGame}>Add Game</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Game Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
            <DialogDescription>Update the game name.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="editGameId">Game ID</label>
              <Input id="editGameId" value={currentGame?.game_id || ""} disabled className="bg-muted" />
            </div>
            <div className="grid gap-2">
              <label htmlFor="editGameVersion">Version</label>
              <Input id="editGameVersion" value={currentGame?.version || ""} onChange={(e) => setNewGameVersion(e.target.value)} placeholder="Enter new game version" />
            </div>
            <div className="grid gap-2">
              <label htmlFor="editGameName">Game Name</label>
              <Input id="editGameName" value={newGameName} onChange={(e) => setNewGameName(e.target.value)} placeholder="Enter new game name" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditGame}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GameManager;
